<template>
  <div class="p-4">
    <!-- Фильтр по дате -->
    <div class="mb-4">
      <input
        id="dateFilter"
        type="date"
        v-model="selectedDate"
        @change="onDateChange"
        class="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
    </div>

    <!-- Состояние загрузки -->
    <div v-if="loading" class="flex justify-center items-center h-40">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <!-- Таблица заданий -->
    <template v-else>
      <table v-if="tasks.length" class="w-full border-collapse bg-white shadow-md">
        <thead class="bg-gray-100">
        <tr>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">#</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Статус</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Заказ</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Количество</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Дата создания</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Порядок</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Пост</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Начало</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Завершение</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="task in tasks" :key="task.id" class="border-b hover:bg-gray-50">
          <td class="px-4 py-2 text-sm text-gray-700">{{ task.id }}</td>
          <td class="px-4 py-2 text-sm text-gray-700 font-semibold">
            <div :class="{
              'text-neutral-900': task.status === 'new',
              'text-blue-600': task.status === 'work',
              'text-green-600': task.status === 'done'
            }">
              {{ task.status ? getStatusLabel(task.status) : '-' }}
            </div>
          </td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ task.production_order_id || '-' }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ task.count || '-' }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ formatDate(task.created_at) }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ task.sort || '-' }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ task.workshop_post_unit_name || '-' }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ task.operations.length > 0 ? getTaskFactStart(task.operations) : '-' }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ task.operations.length > 0 && task.status === 'done' ? getTaskFactEnd(task.operations) : '-' }}</td>
        </tr>
        </tbody>
      </table>
      <template v-else>
        <EmptyState/>
      </template>

      <Pagination
        v-if="pagination.lastPage > 1"
        :current-page="pagination.currentPage"
        :last-page="pagination.lastPage"
        :total="pagination.total"
        :per-page="pagination.perPage"
        @change-page="changePage"
      />
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import EmptyState from "@/components/EmptyState.vue";
import Pagination from '@/components/Pagination.vue';
import moment from 'moment';
import { createUrlStateManager } from '@/utils/urlStateManager';

export default {
  components: { EmptyState, Pagination },
  data() {
    return {
      tasks: [],
      loading: true,
      selectedDate: this.getTodayDate(),
      page: 1,
      perPage: 20,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        perPage: 20,
      },
    };
  },
  created() {
    createUrlStateManager(this, {
      selectedDate: { urlKey: 'date' },
      page: { type: 'number', default: 1, urlKey: 'page' },
    }, {
      routeName: 'Production',
      additionalQuery: { tab: 'tasks' }
    });

    this.fetchTasks();
  },
  methods: {
    // Получение текущей даты в формате YYYY-MM-DD
    getTodayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    onDateChange() {
      this.page = 1;
      this.fetchTasks();
    },
    async fetchTasks() {
      try {
        this.loading = true;
        const response = await axios.post('/api/tasks/getTasks', {
          date: this.selectedDate,
          page: this.page,
          per_page: this.perPage,
        });
        const d = response.data.data;
        this.tasks = d.data || [];
        this.pagination = {
          currentPage: d.current_page,
          lastPage: d.last_page,
          total: d.total,
          perPage: d.per_page,
        };
      } catch (error) {
        console.error('Ошибка при загрузке задач:', error);
      } finally {
        this.loading = false;
      }
    },
    changePage(page) {
      this.page = page;
      this.fetchTasks();
    },
    getStatusLabel(status) {
      switch (status) {
        case 'new':
          return 'Очередь';
        case 'work':
          return 'В работе';
        case 'done':
          return 'Выполнено';
        default:
          return 'Неизвестно';
      }
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString(); // Форматируем дату в удобочитаемый вид
    },
    getTaskFactStart(operations) {
      const dates = operations
        .map(op => op.fact_start_datetime)
        .filter(Boolean);
      if (dates.length === 0) return '-';
      const earliest = dates.reduce((min, date) => moment(date).isBefore(min) ? moment(date) : min, moment(dates[0]));
      return earliest.local().format('YYYY-MM-DD HH:mm:ss');
    },
    getTaskFactEnd(operations) {
      const dates = operations
        .map(op => op.fact_end_datetime)
        .filter(Boolean);
      if (dates.length === 0) return '-';
      const latest = dates.reduce((max, date) => moment(date).isAfter(max) ? moment(date) : max, moment(dates[0]));
      return latest.local().format('YYYY-MM-DD HH:mm:ss');
    },
  },
};
</script>
